import React, { useState } from "react";
import {
  Card,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  Grid,
  Divider,
  Pagination,
  CircularProgress,
} from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import { Visibility, CheckCircle, AccessTime, CalendarToday } from "@mui/icons-material";
import { useGetUsersListQuery } from "lib/store/service/auth";

function UsersDirectory() {
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching } = useGetUsersListQuery({ page, limit: 10 });
  const [selectedUser, setSelectedUser] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const rawUsersList = data?.data?.users || data?.response?.data?.users || data?.data || [];
  const userTotalPages = data?.data?.totalPages || data?.response?.data?.totalPages || Math.ceil(rawUsersList.length / 10) || 1;
  const activeUsersList = rawUsersList.length > 10 ? rawUsersList.slice((page - 1) * 10, page * 10) : rawUsersList;

  const handleOpenDetails = (user) => {
    setSelectedUser(user);
    setDetailsOpen(true);
  };

  const columns = [
    { name: "USER", align: "left" },
    { name: "LOGIC COINS", align: "center" },
    { name: "CLASSIC PROGRESS", align: "center" },
    { name: "CREATED LEVELS", align: "center" },
    { name: "ACCOUNT CREATED", align: "center" },
    { name: "LAST ACTIVE", align: "center" },
    { name: "ACTIONS", align: "center" },
  ];

  const rows = activeUsersList.map((user) => ({
    USER: (
      <VuiBox display="flex" alignItems="center" gap={2}>
        <Avatar src={user.picture} alt={user.name} sx={{ width: 36, height: 36 }} />
        <VuiBox display="flex" flexDirection="column">
          <VuiTypography variant="button" color="white" fontWeight="bold">
            {user.name}
          </VuiTypography>
          <VuiTypography variant="caption" color="text">
            {user.email}
          </VuiTypography>
        </VuiBox>
      </VuiBox>
    ),
    "LOGIC COINS": (
      <VuiTypography variant="button" color="warning" fontWeight="bold">
        🪙 {user.coins || 0}
      </VuiTypography>
    ),
    "CLASSIC PROGRESS": (
      <Chip
        label={`${user.completedCount} / 135 Completed`}
        color="success"
        size="small"
        sx={{ fontWeight: "bold" }}
      />
    ),
    "CREATED LEVELS": (
      <VuiTypography variant="button" fontWeight="bold" sx={{ color: "#38bdf8 !important" }}>
        {user.totalCreatedLevels || 0} Levels
      </VuiTypography>
    ),
    "ACCOUNT CREATED": (
      <VuiTypography variant="caption" color="text" fontWeight="medium">
        {user.createdAt}
      </VuiTypography>
    ),
    "LAST ACTIVE": (
      <Chip
        label={user.lastActiveAt || user.updatedAt || "Just now"}
        color="info"
        size="small"
        sx={{ fontWeight: "bold" }}
      />
    ),
    ACTIONS: (
      <Button
        variant="contained"
        size="small"
        startIcon={<Visibility />}
        onClick={() => handleOpenDetails(user)}
        sx={{
          fontSize: "11px",
          fontWeight: "bold",
          backgroundColor: "#06b6d4 !important",
          color: "#ffffff !important",
          borderRadius: "8px",
          px: 2,
          boxShadow: "0 0 12px rgba(6, 182, 212, 0.4)",
          "&:hover": {
            backgroundColor: "#0891b2 !important",
            boxShadow: "0 0 18px rgba(6, 182, 212, 0.7)",
          },
        }}
      >
        View Details
      </Button>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <Card>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
            <VuiTypography variant="h5" color="white" fontWeight="bold">
              User Accounts Directory
            </VuiTypography>
          </VuiBox>
          <VuiBox p={2}>
            {isLoading || isFetching ? (
              <VuiBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8} gap={2}>
                <CircularProgress color="info" size={40} />
                <VuiTypography variant="button" color="text" fontWeight="bold">
                  Loading User Accounts...
                </VuiTypography>
              </VuiBox>
            ) : rows.length === 0 ? (
              <VuiBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <VuiTypography variant="h3" mb={1}>👥</VuiTypography>
                <VuiTypography variant="h6" color="white" fontWeight="bold" mb={0.5}>
                  No Registered User Accounts Found
                </VuiTypography>
                <VuiTypography variant="caption" color="text">
                  Registered player accounts will be listed here.
                </VuiTypography>
              </VuiBox>
            ) : (
              <Table columns={columns} rows={rows} />
            )}
          </VuiBox>
        </Card>

        <Card sx={{ mt: 2, p: 2, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Pagination
            count={userTotalPages}
            page={page}
            onChange={(e, val) => setPage(val)}
            variant="outlined"
            shape="rounded"
            sx={{
              "& .MuiPaginationItem-root": { color: "#ffffff !important", borderColor: "#334155" },
              "& .MuiPaginationItem-root.Mui-selected": { backgroundColor: "#06b6d4 !important", color: "#ffffff !important" },
              "& .MuiPaginationItem-root:hover": { backgroundColor: "rgba(6, 182, 212, 0.2)" },
            }}
          />
        </Card>
      </VuiBox>

      {/* User Info Details Modal Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: "#0f172a",
            color: "#fff",
            borderRadius: "20px",
            border: "1px solid #06b6d4",
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: "1px solid #1e293b", pb: 2 }}>
          <VuiBox display="flex" alignItems="center" gap={2}>
            <Avatar src={selectedUser?.picture} sx={{ width: 54, height: 54 }} />
            <VuiBox display="flex" flexDirection="column">
              <VuiTypography variant="h5" color="white" fontWeight="bold">
                {selectedUser?.name}
              </VuiTypography>
              <VuiTypography variant="caption" color="cyan">
                {selectedUser?.email} (ID: {selectedUser?._id})
              </VuiTypography>
            </VuiBox>
          </VuiBox>
        </DialogTitle>

        <DialogContent sx={{ py: 3 }}>
          {/* Account Timestamps Banner */}
          <VuiBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            p={2}
            mb={3}
            sx={{ backgroundColor: "#1e293b", borderRadius: "12px", border: "1px solid #334155" }}
          >
            <VuiBox display="flex" alignItems="center" gap={1}>
              <CalendarToday sx={{ color: "#38bdf8", fontSize: 18 }} />
              <VuiTypography variant="caption" color="text">
                Account Created: <strong style={{ color: "#fff" }}>{selectedUser?.createdAt}</strong>
              </VuiTypography>
            </VuiBox>

            <VuiBox display="flex" alignItems="center" gap={1}>
              <AccessTime sx={{ color: "#34d399", fontSize: 18 }} />
              <VuiTypography variant="caption" color="text">
                Last Active: <strong style={{ color: "#34d399" }}>{selectedUser?.lastActiveAt || selectedUser?.updatedAt || "Active Just Now"}</strong>
              </VuiTypography>
            </VuiBox>
          </VuiBox>

          {/* Quick Stats Grid */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={3}>
              <Card sx={{ p: 2, textAlign: "center", bg: "#1e293b" }}>
                <VuiTypography variant="caption" color="text">LOGIC COINS</VuiTypography>
                <VuiTypography variant="h4" color="warning" fontWeight="bold">
                  🪙 {selectedUser?.coins || 0}
                </VuiTypography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card sx={{ p: 2, textAlign: "center", bg: "#1e293b" }}>
                <VuiTypography variant="caption" color="text">CLASSIC PROGRESS</VuiTypography>
                <VuiTypography variant="h4" color="success" fontWeight="bold">
                  {selectedUser?.completedCount || 0} / 135
                </VuiTypography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card sx={{ p: 2, textAlign: "center", bg: "#1e293b" }}>
                <VuiTypography variant="caption" color="text">APPROVED CREATIONS</VuiTypography>
                <VuiTypography variant="h4" fontWeight="bold" sx={{ color: "#38bdf8 !important" }}>
                  {selectedUser?.approvedLevelsCount || 0}
                </VuiTypography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card sx={{ p: 2, textAlign: "center", bg: "#1e293b" }}>
                <VuiTypography variant="caption" color="text">REJECTED CREATIONS</VuiTypography>
                <VuiTypography variant="h4" color="error" fontWeight="bold">
                  {selectedUser?.rejectedLevelsCount || 0}
                </VuiTypography>
              </Card>
            </Grid>
          </Grid>

          <Divider sx={{ bg: "#334155", my: 2 }} />

          {/* Completed Classic Levels */}
          <VuiBox mb={3}>
            <VuiTypography variant="button" color="white" fontWeight="bold" mb={1} display="block">
              🎮 Completed Classic Levels ({selectedUser?.completedClassicLevels?.length || 0})
            </VuiTypography>
            <VuiBox display="flex" flexWrap="wrap" gap={1}>
              {selectedUser?.completedClassicLevels?.length > 0 ? (
                selectedUser.completedClassicLevels.map((lvl, idx) => (
                  <Chip
                    key={idx}
                    label={`Level ${lvl.replace("classic_lvl_", "")}`}
                    color="success"
                    size="small"
                    icon={<CheckCircle />}
                  />
                ))
              ) : (
                <VuiTypography variant="caption" color="text">No classic levels completed yet</VuiTypography>
              )}
            </VuiBox>
          </VuiBox>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: "1px solid #1e293b" }}>
          <Button
            onClick={() => setDetailsOpen(false)}
            variant="contained"
            sx={{
              fontSize: "12px",
              fontWeight: "bold",
              backgroundColor: "#06b6d4 !important",
              color: "#ffffff !important",
              borderRadius: "10px",
              px: 3,
              py: 1,
              boxShadow: "0 0 14px rgba(6, 182, 212, 0.4)",
              "&:hover": {
                backgroundColor: "#0891b2 !important",
                boxShadow: "0 0 20px rgba(6, 182, 212, 0.7)",
              },
            }}
          >
            Close Details
          </Button>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default UsersDirectory;
